import React from 'react';
import { useTranslation } from 'react-i18next';

// ── Reusable pager (owner 2026-09-01 upgrade) ───────────────────────────────────────────────────
// Every paginated list shares this ONE control: first/prev/next/last buttons plus a page-size
// selector (25 / 50 / 100 / 200, default 100). The chosen size is remembered in localStorage and
// becomes the default everywhere — one preference, all lists.
//
// The default was 50 until Calem's report e389d2e6 (2026-09-30): any list longer than 50 opens on
// 100 a page. A 50 remembered from before then is lifted to 100 ONCE, so every browser lands on 100;
// the version stamp marks the lift done, and a size picked after it — 50 included — is kept as
// picked. The server's fallback (pageLimit in server/src/middleware.ts) moved to 100 with it.
export const PAGE_SIZES = [25, 50, 100, 200];
const LS_KEY = 'mf_page_size';
const LS_VER_KEY = 'mf_page_size_ver';   // the one-off fixes this browser's stored size has had

/** The user's remembered rows-per-page (default 100). Lists read this for their initial load. */
export function getPageSize(): number {
  try {
    if (Number(localStorage.getItem(LS_VER_KEY)) < 2) {   // v2: the 50 → 100 lift, once
      if (localStorage.getItem(LS_KEY) === '50') localStorage.setItem(LS_KEY, '100');
      localStorage.setItem(LS_VER_KEY, '2');
    }
    const n = Number(localStorage.getItem(LS_KEY)); return PAGE_SIZES.includes(n) ? n : 100;
  }
  catch { return 100; }
}

export default function Pagination({ page, total, limit, onPage, onLimit }: {
  page: number; total: number; limit: number; onPage: (p: number) => void;
  /** Called when the user picks a new rows-per-page; the list should reload page 1 at that size. */
  onLimit?: (n: number) => void;
}) {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / limit));
  // Owner 2026-09-07: a single page needs no pager — hide the whole control whenever everything
  // already fits (the page-size preference is global, so it stays settable from any longer list).
  // Kept visible while page > 1 so a stale deep page can always navigate back.
  if (pages <= 1 && page <= 1) return null;
  const btn = (disabled: boolean): React.CSSProperties => ({
    padding: '6px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
    background: 'var(--bg-app)', color: disabled ? 'var(--text-tertiary)' : 'var(--text-secondary)',
    cursor: disabled ? 'default' : 'pointer', fontSize: 13,
  });
  const pick = (n: number) => {
    try { localStorage.setItem(LS_KEY, String(n)); } catch { /* private mode */ }
    onLimit?.(n);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '10px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg-surface)', flexWrap: 'wrap' }}>
      <button disabled={page <= 1} onClick={() => onPage(1)} title={t('pg_first')} style={btn(page <= 1)}><i className="ti ti-chevrons-left" aria-hidden /></button>
      <button disabled={page <= 1} onClick={() => onPage(page - 1)} style={btn(page <= 1)}><i className="ti ti-chevron-left" aria-hidden /></button>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('page')} {page} / {pages} · {total}</span>
      <button disabled={page >= pages} onClick={() => onPage(page + 1)} style={btn(page >= pages)}><i className="ti ti-chevron-right" aria-hidden /></button>
      <button disabled={page >= pages} onClick={() => onPage(pages)} title={t('pg_last')} style={btn(page >= pages)}><i className="ti ti-chevrons-right" aria-hidden /></button>
      {onLimit && (
        <select value={limit} onChange={e => pick(Number(e.target.value))} title={t('pg_size')}
          style={{ padding: '5px 8px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontSize: 12.5, cursor: 'pointer' }}>
          {PAGE_SIZES.map(n => <option key={n} value={n}>{n} / {t('page')}</option>)}
        </select>
      )}
    </div>
  );
}
